import { useEffect, useState } from "react";
import { Mail, Users, Eye, UserPlus, Smartphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";
import { Card, Btn } from "./AdminUI";

const yesterday = () => { const d = new Date(Date.now() + 2 * 3600e3 - 864e5); return d.toISOString().slice(0, 10); };

const Mini = ({ label, value, icon: Icon, testid }) => (
  <div className="rounded-xl border border-[#273042] bg-[#0A0C10] p-4">
    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-[#E11D2A]" /> {label}</div>
    <div className="text-2xl font-extrabold text-white mt-1" data-testid={testid}>{(value || 0).toLocaleString()}</div>
  </div>
);

export const DailyVisitorReport = () => {
  const [day, setDay] = useState(yesterday());
  const [rep, setRep] = useState(null);
  const [sending, setSending] = useState(false);
  const load = (d) => endpoints.adminDailyVisitors(d).then((r) => setRep(r.data)).catch((e) => toast.error(apiError(e?.response?.data?.detail)));
  useEffect(() => { load(day); }, [day]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await endpoints.adminDailyVisitorsSend(day);
      toast.success(`Report emailed to ${data.sent.length} of ${data.recipients.length} admin(s)`);
      load(day);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    setSending(false);
  };

  return (
    <Card className="p-5 mb-6" data-testid="daily-visitor-report">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[220px]">
          <h3 className="text-white font-bold">Daily Visitor Report</h3>
          <p className="text-xs text-slate-400 mt-0.5">Visitors per page for one day (Kigali time). Emailed automatically to every admin at 07:00 with yesterday's numbers.</p>
        </div>
        <input type="date" value={day} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDay(e.target.value)} data-testid="daily-report-date"
          className="h-10 px-3 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm [color-scheme:dark]" />
        <Btn onClick={send} disabled={sending || !rep} data-testid="daily-report-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email now
        </Btn>
      </div>
      {!rep ? <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E11D2A]" /></div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Mini label="Visitors" value={rep.visitors} icon={Users} testid="daily-report-visitors" />
            <Mini label="Page views" value={rep.views} icon={Eye} testid="daily-report-views" />
            <Mini label="New visitors" value={rep.newVisitors} icon={UserPlus} testid="daily-report-new" />
            <Mini label="Phones" value={rep.devices.phone} icon={Smartphone} testid="daily-report-phones" />
          </div>
          <div className="rounded-xl border border-[#273042] overflow-hidden max-h-96 overflow-y-auto">
            <table className="w-full text-sm" data-testid="daily-report-pages-table">
              <thead className="bg-[#0A0C10] text-slate-400 text-xs uppercase sticky top-0"><tr><th className="text-left p-3">Page</th><th className="text-right p-3">Visitors</th><th className="text-right p-3">Views</th></tr></thead>
              <tbody>
                {rep.pages.length === 0 && <tr><td colSpan={3} className="p-4 text-slate-400 text-center">No visits recorded for this day.</td></tr>}
                {rep.pages.map((p) => (
                  <tr key={p.path} className="border-t border-[#273042]"><td className="p-3 text-white font-mono text-xs">{p.path}</td><td className="p-3 text-right text-white font-bold">{p.visitors}</td><td className="p-3 text-right text-slate-300">{p.views}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {rep.utilities && (
            <div className="mt-4 rounded-xl border border-[#273042] bg-[#0A0C10] p-3 text-sm" data-testid="daily-report-utilities">
              <div className="text-white font-bold">Utilities sales: {rep.utilities.count} · {rep.utilities.amount.toLocaleString()} RWF</div>
              <div className="text-xs text-slate-400 mt-1">{rep.utilities.services.length ? rep.utilities.services.map((s) => `${s.name}: ${s.count} · ${s.amount.toLocaleString()} RWF`).join("  |  ") : "No utility sales this day."}
                {rep.utilities.refundNeeded > 0 && <span className="text-amber-300"> · {rep.utilities.refundNeeded} refund(s) needed</span>}</div>
            </div>
          )}
          <div className="mt-3 text-xs text-slate-400" data-testid="daily-report-recipients">
            Recipients: {rep.recipients.length ? rep.recipients.join(", ") : "no admin has an email address yet"}{!rep.emailConfigured && " · email sender not configured"}
          </div>
          {rep.logs?.length > 0 && (
            <div className="mt-2 text-xs text-slate-500" data-testid="daily-report-last-sent">
              Last sent: {new Date(rep.logs[0].at).toLocaleString()} · {rep.logs[0].day} · {rep.logs[0].sent.length}/{rep.logs[0].recipients.length} delivered ({rep.logs[0].trigger})
            </div>
          )}
        </>
      )}
    </Card>
  );
};
